'use client';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Autoplay, Controller, Mousewheel, A11y } from 'swiper/modules';
import { useState, useEffect } from 'react';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import type { GeneralPost } from '@/payload-types';

type Props = {
  posts: GeneralPost[];
};

export function LatestPostsHeroSlider({ posts }: Props) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    setIsReady(true);
  }, []);

  return (
    <aside
      className={`w-full mt-5 px-5 overflow-hidden transition-opacity duration-300 ${isReady ? 'opacity-100' : 'opacity-0'}`}
    >
      <Swiper
        modules={[Navigation, Autoplay, Controller, Mousewheel, A11y]}
        slidesPerView={6}
        spaceBetween={10}
        navigation
        autoplay={{ delay: 6000 }}
        loop
        mousewheel
        breakpoints={{
          320: { slidesPerView: 1 },
          480: { slidesPerView: 2 },
          640: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
          1280: { slidesPerView: 5 },
          1536: { slidesPerView: 6 },
        }}
        className="w-full mx-auto"
      >
        {posts.map((post, index) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: Swiper の loop はスライドの DOM を複製して前後へ移動させる。key を一意にすると複製側が React に別要素と見なされ、カルーセルが崩れる
          <SwiperSlide key={index} className="w-1/6 aspect-video">
            <Link
              href={`/article/${post.slug}`}
              className="w-full h-full block relative overflow-hidden"
            >
              {typeof post.image === 'object' && post.image?.url ? (
                <Image
                  src={post.image.url}
                  loading="lazy"
                  alt={post.image.alt || post.title || 'Post Image'}
                  width={post.image.width || 336}
                  height={post.image.height || 280}
                  objectFit="cover"
                  objectPosition="center"
                />
              ) : (
                <div className="w-full h-full bg-stone-200 dark:bg-stone-800 flex items-center justify-center">
                  <span className="text-stone-500 dark:text-stone-400 text-2xl">No Image</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 w-full bg-black/50 backdrop-blur-sm text-white p-2 text-sm">
                <span title={post.title} className="line-clamp-2">
                  {post.title}
                </span>
              </div>
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </aside>
  );
}
